"use client";

import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { Controller, useForm } from "react-hook-form";
import {
  type AddQuoteResult,
  type ChildQuote,
  type PolicyHolder,
  ProductType,
  type WebShopPublicUser,
} from "@/api_client";
import { ProductContainer } from "@/components/shared/productContainer/ProductContainer";
import { SsnInput } from "@/components/shared/ssnInput/SsnInput";
import { useEntry } from "@/hooks/useEntry";
import type { ChildEntryContent } from "@/services/cms/contentTypes/entry/ChildEntryContent";
import { useEntryContent } from "@/services/cms/tanstackQuery/hooks/useEntryContent";
import {
  type ChildEntryFormType,
  ResolveChildEntry,
} from "@/validators/products/child/ChildEntryValidator";

export type EntryProps = {
  externalUser?: WebShopPublicUser;
  externalPolicyHolder?: PolicyHolder | null;
  externalContent?: ChildEntryContent;
  externalIsLoading?: boolean;
  externalSubmitHandler?: (quote: ChildQuote) => Promise<AddQuoteResult>;
};

export const ChildEntryForm = ({
  externalUser,
  externalSubmitHandler,
  externalIsLoading,
  externalContent,
  externalPolicyHolder,
}: EntryProps): React.JSX.Element => {
  const { currentUser, currentPolicyHolder, isLoading, submitHandler } =
    useEntry<ChildQuote, ChildEntryContent>({
      externalUser,
      externalSubmitHandler,
      externalIsLoading,
      externalContent,
      externalPolicyHolder,
    });
  const { data: content } =
    useEntryContent<typeof ProductType.CHILD>(externalContent);

  const defaultValues: ChildQuote = {
    policyHolderSsn:
      currentPolicyHolder?.prefilledSsn ?? currentUser?.ssn ?? "",
    productType: ProductType.CHILD,
    coveredPerson: {
      ssn: "",
    },
    guardianInfo: {
      ssn: "",
    },
  };

  const { handleSubmit, control, formState } = useForm<ChildEntryFormType>({
    defaultValues,
    disabled: isLoading,
    resolver: yupResolver(ResolveChildEntry(content)),
  });
  const { errors } = formState;

  return (
    <ProductContainer>
      <ProductContainer.Entry>
        <ProductContainer.Form
          noValidate
          onSubmit={handleSubmit(submitHandler)}
        >
          <Controller
            control={control}
            name="policyHolderSsn"
            render={({ field }) => (
              <SsnInput
                id={field.name}
                disabled={
                  currentPolicyHolder?.ssnLocked ||
                  currentUser?.isAuthenticated ||
                  isLoading
                }
                placeholder={content?.policyHolderSsn?.Placeholder ?? undefined}
                data-testid="policy-holder-ssn-input"
                popoverMessage={content?.policyHolderSsn?.Popover}
                errorMessage={errors.policyHolderSsn?.message}
                {...field}
              >
                {content?.policyHolderSsn?.Label}
              </SsnInput>
            )}
          />
          <Controller
            control={control}
            name="coveredPerson.ssn"
            render={({ field }) => (
              <SsnInput
                id={field.name}
                placeholder={content?.childSsn?.Placeholder ?? undefined}
                popoverMessage={content?.childSsn?.Popover}
                errorMessage={errors.coveredPerson?.ssn?.message}
                data-testid="child-ssn-input"
                {...field}
              >
                {content?.childSsn?.Label}
              </SsnInput>
            )}
          />
          <ProductContainer.CallToAction
            htmlType="submit"
            data-testid="child-entry-submit-button"
            loading={isLoading}
          >
            {content?.submitButton?.Label}
          </ProductContainer.CallToAction>
        </ProductContainer.Form>
      </ProductContainer.Entry>
    </ProductContainer>
  );
};
